import React from 'react';
import { makeContentfulAsset } from '../Contentful';
import { isContentfulImage } from '../utils';
import {
  ContentfulImage,
  lightCaptionStyle,
} from '../Contentful/ContentfulImage';
import { DefaultRenderer } from './DefaultRenderer';
import { VideoComponent } from '../ui/Video';
import { renderToString } from 'react-dom/server';
import { DropCap } from '../Module/ArticleModule';

export class ArticleRenderer extends DefaultRenderer {
  pNum = 0;
  initialDropcap = false;

  image(href: string, title: string, text: string) {
    const fullWidth = (text || '').indexOf('full-width') > -1;

    // contentful image: display as natural-width responsive asset
    const isContentfulImg = isContentfulImage(href);

    if (isContentfulImg && !fullWidth) {
      const bits = href.split('.');
      const contentType = `image/${bits[bits.length - 1].replace(
        'jpg',
        'jpeg',
      )}`;
      const asset = makeContentfulAsset({
        url: href,
        contentType,
        description: title || text,
      });

      // TODO: unhack caption styling
      return renderToString(
        <ContentfulImage
          asset={asset}
          caption={text}
          captionStyle={lightCaptionStyle}
        />,
      );
    }

    const txt = (text || '').replace('full-width', '');
    const image = super.image(href, title, txt);
    return `
      <figure>
        ${image}
        ${renderToString(
          <figcaption style={lightCaptionStyle}>{txt}</figcaption>,
        )}
      </figure>
    `;
  }

  paragraph(text: string) {
    if (text && !text.startsWith('[')) {
      this.pNum++;
    }

    let renderedText = text;

    // check if dropcap needed/requested
    if (
      text &&
      text.length > 1 &&
      (text.startsWith('^') ||
        (this.pNum === 1 && this.initialDropcap && /^[a-zA-Z\"]/.test(text)))
    ) {
      renderedText = renderToString(
        <DropCap
          dangerouslySetInnerHTML={{ __html: text.replace(/^\^/, '') }}
        />,
      );
    }

    return super.paragraph(renderYoutubeVideoParagraph(renderedText));
  }
}

/**
 * Renders a YouTube video iframe
 *
 * @example: [youtube-video nBu0rl5B70I]
 */
export const renderYoutubeVideoParagraph = (text: string) => {
  const isVideo = text.indexOf('[youtube-video') === 0;
  if (!isVideo) {
    return text;
  }

  const fullWidth = text.indexOf('[youtube-video-full') === 0;
  const url = getVideoUrl(text);

  if (url) {
    return renderToString(
      <VideoComponent
        url={url}
        wrapperStyle={fullWidth ? { maxWidth: 'unset' } : {}}
        iframeStyle={fullWidth ? { maxWidth: 'unset' } : {}}
      />,
    );
  }

  return '';
};

function getVideoUrl(text: string) {
  try {
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    const [
      ,
      tag,
      videoId,
    ] = /\[(youtube-video|youtube-video-full)\s+([\w-_]+)?\]/gi.exec(
      text,
    ) as any[];

    return `https://www.youtube.com/embed/${videoId}?iv_load_policy=3&playsinline=1&rel=0`;
  } catch (e) {
    // don't error out if we can't parse the YouTube ID
    console.error(e); // eslint-disable-line no-console
    return '';
  }
}
